import { useState, useCallback, useEffect, useRef } from "react";
import orionAPI from "../config/orionAPI";

function usePaymentStatus({ polling = false, onStatusChange = null } = {}) {
  const [paymentData, setPaymentData] = useState({
    payment: null,
    subscription: null,
    plan: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);
  const previousStatusRef = useRef(null);
  const pollingIntervalRef = useRef(30000);

  const fetchPaymentStatus = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await orionAPI.get(
        "/api/users/me/subscriptions/payments",
      );
      const newData = response.data.data;

      if (previousStatusRef.current !== newData.payment?.status) {
        if (onStatusChange && previousStatusRef.current !== null) {
          onStatusChange(newData.payment?.status, newData);
        }
        previousStatusRef.current = newData.payment?.status;
      }

      setPaymentData(newData);

      // Reset polling interval on successful request
      pollingIntervalRef.current = 30000;

      return newData;
    } catch (error) {
      console.error("Error fetching payment status:", error);

      // Handle rate limiting (429 error)
      if (error.response?.status === 429) {
        pollingIntervalRef.current = 60000;
        setError("Muitas requisições. Reduzindo frequência de verificação...");
      } else {
        setError(
          error.response?.data?.message ||
            "Erro ao verificar status do pagamento",
        );
      }

      throw error;
    } finally {
      setLoading(false);
    }
  }, [onStatusChange]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    if (intervalRef.current) {
      return;
    }

    const startPollingWithInterval = () => {
      const currentInterval = pollingIntervalRef.current;

      intervalRef.current = setInterval(async () => {
        try {
          await fetchPaymentStatus();

          // If interval changed after rate limit, restart polling with new interval
          if (pollingIntervalRef.current !== currentInterval) {
            stopPolling();
            startPollingWithInterval();
          }
        } catch (error) {
          // If interval changed due to rate limit, restart polling
          if (pollingIntervalRef.current !== currentInterval) {
            stopPolling();
            startPollingWithInterval();
          }
        }
      }, currentInterval);
    };

    startPollingWithInterval();
  }, [fetchPaymentStatus, stopPolling]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  useEffect(() => {
    pollingIntervalRef.current = 30000;

    if (polling) {
      startPolling();
    } else {
      fetchPaymentStatus();
    }

    return () => {
      stopPolling();
      pollingIntervalRef.current = 30000;
    };
  }, [polling, startPolling, stopPolling, fetchPaymentStatus]);

  return {
    paymentData,
    loading,
    error,
    fetchPaymentStatus,
    startPolling,
    stopPolling,
    clearError,
  };
}

export default usePaymentStatus;
